import React, { useContext, useState } from "react";
import {
  Box,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { TEXT_COLOR } from "../../theme";
import { addProjectModalStyles } from "./styles";
import CustomButton from "../CustomButton/CustomButton";
import { CustomTextField } from "../TracksPageComponents/styles";
import { postFolder } from "../../services/folderService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { FileState, Folder, PostFolderResponse } from "../../proto/api_pb";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";

const AddFolderModal: React.FC<{
  projectId: string;
  refreshFolders: () => void;
  folderId: string | undefined;
}> = ({ projectId, refreshFolders, folderId }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [folderName, setFolderName] = useState("");
  const [isFolderNameEmptyError, setIsFolderNameEmptyError] = useState(false);
  const isMobileScreen = useMediaQuery("(max-width:600px)");
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise, isEnterpriseAccount } = React.useContext(UserDetailsContext);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFolderName("");
    setIsFolderNameEmptyError(false);
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      setIsFolderNameEmptyError(true);
      toast.warn("Folder name cannot be empty");
      return;
    }
    setIsLoading(true);
    const folder = new Folder();
    folder.setName(folderName);
    folder.setFolderState(FileState.FILE_STATE_ACTIVE);
    folder.setParentProjectId(
      projectId === "undefined" || !projectId ? "" : projectId
    );
    folder.setOwnerId(userId);
    if (folderId) folder.setParentFolderId(folderId);
    folder.setEnterpriseId(enterprise?.id as string);
    const token = await getValidAccessToken();
    const res = await postFolder(token, folder);
    if (res instanceof PostFolderResponse) {
      toast.success("Folder created successfully");
      handleClose();
      refreshFolders();
      setIsLoading(false);
    } else {
      setIsLoading(false);
      toast.error("Fail to create folder");
    }
  };

  return (
    <>
      {isEnterpriseAccount &&
        <CustomButton
          icon={CreateNewFolderOutlinedIcon}
          text="Create Folder"
          onClick={handleOpen}
        />
      }
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            ...addProjectModalStyles.modal,
            width: isMobileScreen ? "100%" : 400,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            sx={{ color: TEXT_COLOR, marginBottom: "20px" }}
            variant="h6"
          >
            Create New Folder
          </Typography>
          <Typography sx={{ color: TEXT_COLOR }}>
            Folder Name<span className="asterisk">*</span>
          </Typography>
          <CustomTextField
            style={{ width: "100%" }}
            error={isFolderNameEmptyError}
            onChange={(e) => {
              setIsFolderNameEmptyError(false);
              setFolderName(e.target.value);
            }}
            fullWidth
            placeholder="Enter Folder Name"
          />
          <Box sx={addProjectModalStyles.bottomContainer}>
            <CustomButton
              loading={isLoading}
              text="Create folder"
              onClick={handleCreateFolder}
              isDisabled={!folderName.trim()}
              customStyles={{ width: "150px" }}
            />
            <CustomButton
              text="Cancel"
              onClick={handleClose}
              isSkipButton={true}
              customStyles={{ width: "150px" }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};
export default AddFolderModal;
